<template>
  <div class="custom-table__row">
    <div
      :data-column="$t('ci_cd.pipeline.pipelineStatus')"
      :class="{ 'd-flex align-items-center': !item.duration || !item.finishedAt }"
      class="cell_time-job custom-table__cell text-truncate"
    >
      <div>
        <ci-main-status
          :status="item.status"
          class="cell_time-job-status"
          :class="{ 'mb-2': item.duration || item.finishedAt }"
        />
        <div
          v-if="item.duration"
          style="font-size: 12px"
          class="text-secondary cell_time-job-clock"
        >
          <i
            style="font-size: 16px"
            class="gf-icon gf-icon-clock mr-1"
          />
          {{ $moment.utc(item.duration * 1000).format('HH:mm:ss') }}
        </div>
        <div
          v-if="item.finishedAt"
          style="font-size: 12px"
          class="text-secondary text-truncate cell_time-job-calendar"
        >
          <i
            style="font-size: 16px"
            class="gf-icon gf-icon-calendar mr-1"
          />
          {{ $moment.utc(item.finishedAt).local().format('DD.MM.YY в HH:mm') }}
        </div>
      </div>
    </div>
    <div
      :data-column="$t('ci_cd.pipeline.pipelineConveyor')"
      class="cell_job-id custom-table__cell text-truncate"
    >
      <div
        :class="{
          'text-wrap': !isInRange
        }"
      >
        <div v-if="isInRange">
          <div class="d-flex align-items-center text-truncate">
            <i
              class="text-secondary"
              :class="{
                'gf-icon gf-icon-git-branch': !item.isTag,
                'gf-icon gf-icon-tag': item.isTag
              }"
            ></i>
            <span class="pipeline-list-item__branch text-truncate font-weight-bold ml-1">
              {{ item.ref }}
            </span>
            <commit-short-link
              v-if="item.commit"
              class="ml-1"
              :hash="item.commit.hash"
              :project-alias="$route.params.projectAlias"
              :user-alias="$route.params.alias"
            />
          </div>
          <div
            style="gap: 0.5rem"
            v-if="item.commit"
            class="d-flex align-items-center"
          >
            <user-label-item
              v-if="labelCommitUser"
              :image-size="16"
              container=".custom-table"
              :tooltip-prefix="'pipeline-commit-author' + item.localId"
              :user="labelCommitUser"
              :show-name="false"
              :has-user-link="true"
              :show-tooltip="true"
              :open-in-new-tab="false"
            />
            <gf-custom-tooltip container=".custom-table">
              <template #element>
                <div
                  v-truncate="item.commit.shortMessage"
                  class="pipeline-list-item__commit text-truncate"
                ></div>
              </template>
              <template #content>
                {{ item.commit.message }}
              </template>
            </gf-custom-tooltip>
          </div>
        </div>
        <a
          :class="{ 'mb-1': item?.mergeResultPipeline }"
          style="font-size: 14px"
          :href="`/project/${$route.params.alias}/${$route.params.projectAlias}/ci-cd/pipeline/${item.localId}`"
        >
          #{{ item.localId }}
        </a>
        <span v-if="pipelineLabel" class="bg-primary pipeline-list-item__badge rounded-pill badge-primary">
          {{ pipelineLabel }}
        </span>
      </div>
    </div>
    <div
      :data-column="$t('ci_cd.pipeline.pipelineInitiator')"
      class="cell_job-initiator custom-table__cell"
    >
      <user-label-item
        v-if="item.user"
        container=".custom-table"
        :tooltip-prefix="'pipeline-user-triggerer' + item.localId"
        :user="item.user"
        :has-user-link="true"
        :show-tooltip="true"
        :open-in-new-tab="false"
      />
      <empty-listing
        v-else
        :title="$t('ci_cd.pipeline.noUser')"
        type="blank"
      />
    </div>
    <div
      v-if="!isInRange"
      :data-column="$t('ci_cd.pipeline.pipelineCommit')"
      style="line-height: normal"
      class="cell_job-commit custom-table__cell"
      ref="commit-cell"
    >
      <div>
        <div class="d-flex align-items-center text-truncate">
          <i
            class="text-secondary"
            :class="{
              'gf-icon gf-icon-git-branch': !item.isTag,
              'gf-icon gf-icon-tag': item.isTag
            }"
          ></i>
          <span class="text-truncate font-weight-bold ml-1">{{ item.ref }}</span>
          <commit-short-link
            v-if="item.commit"
            class="ml-1"
            :hash="item.commit.hash"
            :project-alias="$route.params.projectAlias"
            :user-alias="$route.params.alias"
          />
        </div>
        <div
          style="gap: 0.5rem"
          v-if="item.commit"
          class="d-flex align-items-center"
        >
          <user-label-item
            v-if="labelCommitUser"
            :image-size="16"
            container=".custom-table"
            :tooltip-prefix="'pipeline-commit-author' + item.localId"
            :user="labelCommitUser"
            :show-name="false"
            :has-user-link="true"
            :show-tooltip="true"
            :open-in-new-tab="false"
          />
          <div class="text-truncate">
            <div
              v-truncate="item.commit.shortMessage"
              class="pipeline-list-item__commit text-truncate"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div
      :data-column="$t('ci_cd.pipeline.pipelineStages')"
      :class="{ 'd-flex align-items-center': !item.duration || !item.finishedAt }"
      class="cell_job-stages custom-table__cell"
    >
      <div class="d-flex flex-wrap">
        <pipeline-stage-item
          v-for="stage in item.stages"
          :key="stage.id"
          :stage="stage"
          :pipeline-id="item.localId"
        />
      </div>
    </div>
    <div
      class="cell_job-button custom-table__cell"
      :class="{ 'd-flex align-items-center': !item.duration || !item.finishedAt }"
      v-if="
        getActionAvailabilityFlag(ACTION_ENUM.EXECUTE_CI_CD) ||
        getActionAvailabilityFlag(ACTION_ENUM.DELETE_PIPELINE)
      "
    >
      <gf-dropdown
        v-if="
          getActionAvailabilityFlag(ACTION_ENUM.DELETE_PIPELINE) || isNotEmpty || canBeCancelled
        "
        position="right"
        btn-class="d-flex align-items-center btn btn-sm btn-outline-secondary px-0 py-1"
        btn-icon="gf-icon gf-icon-ellipsis-vertical"
        :no-carret="true"
        style="--gfd-minw: 100px; display: none; float: right"
      >
        <template v-if="getActionAvailabilityFlag(ACTION_ENUM.EXECUTE_CI_CD)">
          <a
            v-if="isNotEmpty"
            href="#"
            class="dropdown-item"
            @click.prevent="$emit('restart')"
            :class="{ disabled: !item.canBeRetried }"
          >
            {{ $t('ci_cd.pipeline.restart') }}
          </a>
          <a
            v-if="canBeCancelled"
            href="#"
            class="dropdown-item"
            @click.prevent="$emit('cancel')"
          >
            {{ $t('ci_cd.pipeline.cancel') }}
          </a>
        </template>
        <a
          v-if="getActionAvailabilityFlag(ACTION_ENUM.DELETE_PIPELINE)"
          href="#"
          class="dropdown-item text-danger"
          @click.prevent="$emit('delete')"
        >
          {{ $t('ci_cd.pipeline.delete') }}
        </a>
      </gf-dropdown>
    </div>
  </div>
</template>

<script>
import UserLabelItem from '@shared/shared-components/shared/user-label-item';
import { PIPELINE_STATUS_ENUM } from '@shared/modules/ci-cd/ci-cd.constants';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
import PipelineStageItem from '@shared/modules/ci-cd/components/pipeline-stage-item';
import EmptyListing from '@shared/shared-components/empty-listing';
import CiMainStatus from '@shared/modules/ci-cd/components/ci-main-status';
import CommitShortLink from '@shared/shared-components/commit-short-link';
import CiCdMessages from '@shared/modules/ci-cd/ci-cd.messages';

export default {
  name: 'PipelineListItem',
  directives: {
    truncate(el, string) {
      if (string.value.length > 55) {
        el.innerHTML = `${string.value.substr(0, 52)}...`;
        return;
      }

      el.innerHTML = string.value;
    }
  },
  components: {
    CommitShortLink,
    CiMainStatus,
    EmptyListing,
    PipelineStageItem,
    UserLabelItem
  },
  props: {
    item: {
      default: null
    }
  },
  data() {
    return {
      PIPELINE_STATUS_ENUM,
      ACTION_ENUM,
      labelCommitUser: null,
      windowWidth: window.innerWidth
    };
  },
  mounted() {
    if (this.item?.commit?.user) {
      this.labelCommitUser = this.item?.commit?.user;
    } else if (this.item?.commit?.authorIdent) {
      this.labelCommitUser = this.item.commit.authorIdent;
      this.labelCommitUser.fullName = this.labelCommitUser.name;
    }

    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    isInRange() {
      return this.windowWidth >= 992 && this.windowWidth <= 1400;
    },

    canBeCancelled() {
      return (
          this.item?.status?.id !== this.PIPELINE_STATUS_ENUM.SUCCESS &&
          this.item?.status?.id !== this.PIPELINE_STATUS_ENUM.FAILED &&
          this.item?.status?.id !== this.PIPELINE_STATUS_ENUM.SKIPPED &&
          this.item?.status?.id !== this.PIPELINE_STATUS_ENUM.CANCELED
      );
    },
    isNotEmpty() {
      return this.item?.stages?.length > 0;
    },
    pipelineLabel() {
      return this.item.mergeResultPipeline ? this.$i18n.t('ci_cd.pipeline.mergeResultPipeline') :
          this.item.mergeRequestPipeline ? this.$i18n.t('ci_cd.pipeline.mergeRequestPipeline') :
              this.item.trainsCarPipeline ? this.$i18n.t('ci_cd.pipeline.trainPipeline') : null
    }
  },
  methods: {
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    }
  },
  i18n: {
    messages: CiCdMessages
  }
  }
</script>

<style lang="scss">
$this: '.pipeline-list-item';
$break_lg: 992px;
$break_xxl: 1400px;
$break_xxxl: 1700px;

#{$this} {
  &__badge {
    display: inline-block;
    padding: 3px 6px;
    line-height: 14px;
    border-radius: 10px;
    gap: 10px;
    font-size: 12px;
    font-weight: 550;
    letter-spacing: 0.015rem;
  }
  &__branch {
    @media screen and (min-width: #{$break_lg}) and (max-width: #{$break_xxl}) {
      max-width: 120px;
    }
  }

  &__commit {
    @media screen and (min-width: #{$break_xxl}) and (max-width: #{$break_xxxl}) {
      display: block;
      max-width: 18vw;
    }

    @media screen and (min-width: #{$break_lg}) and (max-width: #{$break_xxl}) {
      display: block;
      max-width: 21vw;
    }
  }
}
</style>
